/**
 * Pages — the site's written pages: the policies, the FAQ and About us.
 *
 * A fixed list. The storefront has a route for each of these and for nothing
 * else, so there is no "add page" or "delete page" here; an admin writes the
 * pages, they do not make them.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, FileText, HelpCircle, Pencil } from "lucide-react";
import swal from "sweetalert";
import axiosInstance from "../../../redux/api";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import { PAGE_PATHS, SITE, errorText, formatDate } from "./shared";
import "../DynamicSections/HomePageCRM.css";
import "./ContentPages.css";

const STATUS_BADGE = {
  published: { className: "ds-badge--active", label: "Published" },
  draft: { className: "ds-badge--inactive", label: "Draft" },
  hold: { className: "ds-badge--inactive", label: "On hold" },
};

const ContentPagesList = () => {
  const navigate = useNavigate();
  const { hasAction } = usePermissions();
  const editable = hasAction(MENU.SETTINGS, PERMISSION.EDIT);

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("admin/pages/");
      setPages(data?.response?.pages || []);
    } catch (error) {
      swal({ text: errorText(error, "Could not load the pages"), icon: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  return (
    <div className="ds-container">
      <div className="ds-header">
        <div>
          <h4>Pages</h4>
          <p>The policies, the FAQ and About us, in both languages.</p>
        </div>
      </div>

      {loading ? (
        <div className="ds-empty-state" style={{ height: 200 }}>Loading pages...</div>
      ) : pages.length === 0 ? (
        <div className="ds-empty-state" style={{ height: 200 }}>No pages found</div>
      ) : (
        <div className="ds-table-wrapper">
          <table className="ds-table">
            <thead>
              <tr>
                <th>Page</th>
                <th>Arabic title</th>
                <th>Status</th>
                <th>Google</th>
                <th>Last updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => {
                const badge = STATUS_BADGE[page.status_label] || STATUS_BADGE.draft;
                const Icon = page.key === "faq" ? HelpCircle : FileText;
                return (
                  <tr key={page.key}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon size={15} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{page.title}</div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {PAGE_PATHS[page.key] || `/${page.key}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td dir="rtl" style={{ textAlign: "right" }}>
                      {page.ar_title || <span style={{ color: "#9ca3af" }}>—</span>}
                    </td>
                    <td>
                      <span className={`ds-badge ${badge.className}`}>{badge.label}</span>
                    </td>
                    <td>
                      {page.is_indexable ? (
                        <span className="ds-badge ds-badge--active">Shown</span>
                      ) : (
                        <span className="ds-badge ds-badge--inactive">Hidden</span>
                      )}
                    </td>
                    <td>{formatDate(page.last_updated || page.updated_at)}</td>
                    <td>
                      <div className="ds-actions">
                        <button
                          type="button"
                          className="ds-btn-edit"
                          onClick={() => navigate(`/content-pages/${page.key}`)}
                        >
                          <Pencil size={13} style={{ marginInlineEnd: 4 }} />
                          {editable ? "Edit" : "View"}
                        </button>
                        {page.status_label === "published" && (
                          <a
                            className="ds-btn-secondary"
                            href={`https://${SITE}/en${PAGE_PATHS[page.key] || `/${page.key}`}`}
                            target="_blank"
                            rel="noreferrer"
                            title="Open on the website"
                          >
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ContentPagesList;
